import React, { useState } from "react";
import {
  Card,
  Tabs,
  Typography,
  Space,
  Row,
  Col,
  Button,
  Slider,
  Progress,
  Select,
  Statistic,
} from "antd";

const { Title, Paragraph, Text } = Typography;
const { Option } = Select;

const ModelTraining = () => {
  const [selectedData, setSelectedData] = useState([]);
  const [batchSize, setBatchSize] = useState(2);
  const [epochs, setEpochs] = useState(10);
  const [learningRate, setLearningRate] = useState(0.1);
  const [isTraining, setIsTraining] = useState(false);
  const [currentEpoch, setCurrentEpoch] = useState(0);
  const [trainingLoss, setTrainingLoss] = useState([]);
  const [validationLoss, setValidationLoss] = useState([]);
  const [trainingAccuracy, setTrainingAccuracy] = useState([]);
  const [validationAccuracy, setValidationAccuracy] = useState([]);
  const [weights, setWeights] = useState({
    sugar: 0.5,
    flour: 0.5,
    butter: 0.5,
    eggs: 0.5,
  });
  const [bias, setBias] = useState(0.5);
  const [modelTrained, setModelTrained] = useState(false);
  const [trainingSet, setTrainingSet] = useState([]);
  const [testingSet, setTestingSet] = useState([]);
  const [validationSet, setValidationSet] = useState([]);
  const [testResults, setTestResults] = useState([]);
  const [showOverfitting, setShowOverfitting] = useState(false);
  const [finalTestAccuracy, setFinalTestAccuracy] = useState(0);
  const [individualTestId, setIndividualTestId] = useState(null);
  const [individualTestResult, setIndividualTestResult] = useState(null);

  // Complete dataset of recipes (data points)
  const allDataPoints = [
    {
      id: 1,
      sugar: 9,
      flour: 4,
      butter: 9,
      eggs: 2,
      label: "Cookie",
      emoji: "🍪",
      color: "#fbbf24",
    },
    {
      id: 2,
      sugar: 8,
      flour: 5,
      butter: 8,
      eggs: 2,
      label: "Cookie",
      emoji: "🍪",
      color: "#fbbf24",
    },
    {
      id: 3,
      sugar: 9,
      flour: 3,
      butter: 9,
      eggs: 1,
      label: "Cookie",
      emoji: "🍪",
      color: "#fbbf24",
    },
    {
      id: 4,
      sugar: 2,
      flour: 9,
      butter: 3,
      eggs: 1,
      label: "Bread",
      emoji: "🍞",
      color: "#f97316",
    },
    {
      id: 5,
      sugar: 1,
      flour: 9,
      butter: 2,
      eggs: 1,
      label: "Bread",
      emoji: "🍞",
      color: "#f97316",
    },
    {
      id: 6,
      sugar: 3,
      flour: 8,
      butter: 4,
      eggs: 1,
      label: "Bread",
      emoji: "🍞",
      color: "#f97316",
    },
    {
      id: 7,
      sugar: 9,
      flour: 6,
      butter: 7,
      eggs: 4,
      label: "Cake",
      emoji: "🎂",
      color: "#ec4899",
    },
    {
      id: 8,
      sugar: 8,
      flour: 7,
      butter: 6,
      eggs: 3,
      label: "Cake",
      emoji: "🎂",
      color: "#ec4899",
    },
    {
      id: 9,
      sugar: 9,
      flour: 5,
      butter: 8,
      eggs: 4,
      label: "Cake",
      emoji: "🎂",
      color: "#ec4899",
    },
    {
      id: 10,
      sugar: 2,
      flour: 5,
      butter: 9,
      eggs: 0,
      label: "Croissant",
      emoji: "🥐",
      color: "#a16207",
    },
    {
      id: 11,
      sugar: 3,
      flour: 4,
      butter: 9,
      eggs: 0,
      label: "Croissant",
      emoji: "🥐",
      color: "#a16207",
    },
    {
      id: 12,
      sugar: 1,
      flour: 6,
      butter: 8,
      eggs: 1,
      label: "Croissant",
      emoji: "🥐",
      color: "#a16207",
    },
  ];

  const possibleLabels = ["Cookie", "Bread", "Cake", "Croissant"];

  React.useEffect(() => {
    setSelectedData(allDataPoints.map((point) => point.id));
  }, []);

  const extractFeatures = (dataPoint) => {
    return {
      sugar: dataPoint.sugar / 10,
      flour: dataPoint.flour / 10,
      butter: dataPoint.butter / 10,
      eggs: dataPoint.eggs / 10,
    };
  };

  const calculateSimilarity = (point1, point2) => {
    const features1 = extractFeatures(point1);
    const features2 = extractFeatures(point2);
    let similarity = 0;
    similarity += 1 - Math.abs(features1.sugar - features2.sugar);
    similarity += 1 - Math.abs(features1.flour - features2.flour);
    similarity += 1 - Math.abs(features1.butter - features2.butter);
    similarity += 1 - Math.abs(features1.eggs - features2.eggs);
    return similarity / 4;
  };

  const predict = (
    dataPoint,
    currentWeights,
    currentBias,
    trainingExamples = []
  ) => {
    if (trainingExamples.length === 0) {
      const randomLabels = ["Cookie", "Bread", "Cake", "Croissant"];
      const randomPrediction =
        randomLabels[Math.floor(Math.random() * randomLabels.length)];
      return {
        prediction: randomPrediction,
        confidence: Math.round(50 + Math.random() * 20),
      };
    }

    const similarities = trainingExamples.map((example) => ({
      example,
      similarity: calculateSimilarity(dataPoint, example.dataPoint),
      label: example.dataPoint.label,
    }));

    similarities.sort((a, b) => b.similarity - a.similarity);
    const topMatches = similarities.slice(0, 3);
    const labelCounts = {};
    topMatches.forEach((match) => {
      labelCounts[match.label] =
        (labelCounts[match.label] || 0) + match.similarity;
    });

    let bestLabel = "Cookie";
    let bestScore = 0;
    Object.entries(labelCounts).forEach(([label, score]) => {
      if (score > bestScore) {
        bestScore = score;
        bestLabel = label;
      }
    });

    const confidence = Math.min(
      95,
      Math.max(60, 60 + (bestScore / topMatches.length) * 35)
    );
    return { prediction: bestLabel, confidence: Math.round(confidence) };
  };

  const trainModel = () => {
    setIsTraining(true);
    setCurrentEpoch(0);
    setTrainingLoss([]);
    setValidationLoss([]);
    setTrainingAccuracy([]);
    setValidationAccuracy([]);
    setShowOverfitting(false);
    setModelTrained(false);

    let epochCount = 0;
    let learnedExamples = [];

    const trainingInterval = setInterval(() => {
      if (epochCount >= epochs) {
        clearInterval(trainingInterval);
        setIsTraining(false);
        setModelTrained(true);
        return;
      }

      const trainingData = trainingSet.map((id) => {
        const point = allDataPoints.find((d) => d.id === id);
        return { dataPoint: point };
      });

      let trainCorrect = 0;
      let trainTotal = trainingData.length;

      trainingData.forEach((example) => {
        if (epochCount === 0) {
          learnedExamples.push(example);
        }
        const prediction = predict(
          example.dataPoint,
          weights,
          bias,
          learnedExamples
        );
        if (prediction.prediction === example.dataPoint.label) {
          trainCorrect++;
        }
      });

      let valCorrect = 0;
      let valTotal = validationSet.length;

      validationSet.forEach((id) => {
        const point = allDataPoints.find((d) => d.id === id);
        const prediction = predict(point, weights, bias, learnedExamples);
        if (prediction.prediction === point.label) {
          valCorrect++;
        }
      });

      const trainAcc = trainTotal > 0 ? (trainCorrect / trainTotal) * 100 : 0;
      const valAcc = valTotal > 0 ? (valCorrect / valTotal) * 100 : 0;
      const trainLoss = Math.max(
        0.1,
        1.0 - (trainAcc / 100) * 0.9 + Math.random() * 0.1
      );
      const valLoss = Math.max(
        0.1,
        1.0 - (valAcc / 100) * 0.9 + Math.random() * 0.1
      );

      setCurrentEpoch(epochCount + 1);
      setTrainingLoss((prev) => [...prev, trainLoss]);
      setValidationLoss((prev) => [...prev, valLoss]);
      setTrainingAccuracy((prev) => [...prev, trainAcc]);
      setValidationAccuracy((prev) => [...prev, valAcc]);

      if (trainAcc > 85 && valAcc < 65 && epochCount > epochs / 2) {
        setShowOverfitting(true);
      }

      if (epochCount < epochs / 2) {
        const progress = (epochCount + 1) / (epochs / 2);
        const adjustedTrainAcc = 50 + progress * 30 + Math.random() * 5;
        const adjustedValAcc = 45 + progress * 25 + Math.random() * 10;
        setTrainingAccuracy((prev) => [...prev.slice(0, -1), adjustedTrainAcc]);
        setValidationAccuracy((prev) => [...prev.slice(0, -1), adjustedValAcc]);
      }

      epochCount++;
    }, 800);
  };

  const testModelOnSet = () => {
    setIndividualTestResult(null);
    const learnedExamples = trainingSet.map((id) => {
      const point = allDataPoints.find((d) => d.id === id);
      return { dataPoint: point };
    });

    const results = testingSet.map((id) => {
      const point = allDataPoints.find((d) => d.id === id);
      const prediction = predict(point, weights, bias, learnedExamples);
      return {
        dataPoint: point,
        prediction: prediction.prediction,
        confidence: prediction.confidence,
        correct: prediction.prediction === point.label,
      };
    });

    setTestResults(results);
    const accuracy =
      (results.filter((r) => r.correct).length / results.length) * 100;
    setFinalTestAccuracy(accuracy);
  };

  const renderDataset = () => (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Card
        title={
          <span style={{ color: "#0071e3" }}>How to navigate this section</span>
        }
      >
        <Paragraph style={{ marginBottom: 10 }}>
          Work on each tab, then click on the next one in the following
          sequence.
        </Paragraph>
        <Paragraph>
          <Text strong>Build Dataset</Text> → <Text strong>Split Data</Text> →{" "}
          <Text strong>Configure Training</Text> →{" "}
          <Text strong>Train Model</Text> → <Text strong>Test & Evaluate</Text>
        </Paragraph>
      </Card>

      <Card
        title={
          <span style={{ color: "#0071e3" }}>
            Understanding Data Points & Dataset
          </span>
        }
      >
        <Paragraph>
          A <Text strong>data point</Text> is a single recipe example. Each row
          below is one data point. <Text strong>Features</Text> are the
          ingredient amounts (INPUTS) and the <Text strong>label</Text> is the
          recipe type (OUTPUT the AI learns to predict).
        </Paragraph>
        <Paragraph style={{ marginBottom: 0 }}>
          <Text strong>Think of it like a cookbook:</Text>
        </Paragraph>
        <ul style={{ paddingLeft: "20px" }}>
          <li>
            Each <Text strong>data point</Text> = one recipe in the cookbook
          </li>
          <li>
            Each <Text strong>feature</Text> = ingredient amount (sugar, flour,
            butter, eggs)
          </li>
          <li>
            Each <Text strong>label</Text> = type of recipe (cookie, bread,
            cake, croissant)
          </li>
          <li>
            The entire <Text strong>dataset</Text> = the complete cookbook with
            all recipes
          </li>
        </ul>
      </Card>

      <Card
        title={
          <span style={{ color: "#0071e3" }}>
            Your Complete Dataset ({allDataPoints.length} Data Points)
          </span>
        }
        style={{ height: "620px" }}
      >
        <div
          style={{
            height: "500px", // fixed height (adjust as needed)
            overflowY: "auto", // enable vertical scrolling
          }}
        >
          <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            {allDataPoints.map((point) => (
              <div
                key={point.id}
                style={{
                  padding: "16px",
                  border: `2px solid ${point.color}`,
                  borderRadius: "8px",
                  backgroundColor: `${point.color}15`,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "12px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                    }}
                  >
                    <span style={{ fontSize: "32px" }}>{point.emoji}</span>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: "16px" }}>
                        {point.label}
                      </div>
                      <div style={{ fontSize: "12px", color: "#666" }}>
                        Data Point #{point.id}
                      </div>
                    </div>
                  </div>
                  <div style={{ color: "#52c41a", fontWeight: 600 }}>
                    ✓ In Dataset
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: "16px",
                    flexWrap: "wrap",
                    marginBottom: "8px",
                  }}
                >
                  <Text>🍯 Sugar: {point.sugar}/10</Text>
                  <Text>🌾 Flour: {point.flour}/10</Text>
                  <Text>🧈 Butter: {point.butter}/10</Text>
                  <Text>🥚 Eggs: {point.eggs}</Text>
                </div>
                <Text>
                  → Label: <Text strong>{point.label}</Text>
                </Text>
              </div>
            ))}
          </Space>
        </div>
      </Card>

      <Card
        title={<span style={{ color: "#0071e3" }}>Dataset Statistics</span>}
      >
        <Paragraph style={{ marginBottom: 0 }}>
          You have 12 diverse recipe examples. The model will learn patterns
          from these examples to recognize different recipe types based on
          ingredient amounts.
        </Paragraph>
        <br />
        <Row gutter={[16, 16]}>
          {possibleLabels.map((label) => {
            const count = allDataPoints.filter((d) => d.label === label).length;
            const example = allDataPoints.find((d) => d.label === label);
            return (
              <Col xs={12} md={6} key={label}>
                <div style={{ textAlign: "center" }}>
                  <div
                    style={{
                      fontSize: "32px",
                      fontWeight: 700,
                      color: "#0071e3",
                    }}
                  >
                    {example.emoji} {count}
                  </div>
                  <div style={{ fontSize: "14px" }}>{label} examples</div>
                </div>
              </Col>
            );
          })}
        </Row>
      </Card>
    </Space>
  );

  const renderSplitData = () => (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Card
        title={
          <span style={{ color: "#0071e3" }}>
            Understanding the Three Data Sets
          </span>
        }
      >
        <Paragraph style={{ marginBottom: "8px" }}>
          <Text>Think of it like preparing for a big exam:</Text>
        </Paragraph>
        <ul style={{ paddingLeft: "20px", marginBottom: 0 }}>
          <li>
            <Text strong>Training Set:</Text> Your study materials (textbook,
            notes)
          </li>
          <li>
            <Text strong>Validation Set:</Text> Practice tests you take during
            studying
          </li>
          <li>
            <Text strong>Testing Set:</Text> The actual final exam
          </li>
        </ul>
      </Card>

      <Card>
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <div
            style={{
              display: "flex",
              gap: "16px",
              justifyContent: "space-between",
              flexWrap: "wrap",
            }}
          >
            <div>
              <Text strong>Training:</Text> {trainingSet.length} examples (
              {trainingSet.length > 0
                ? Math.round((trainingSet.length / selectedData.length) * 100)
                : 0}
              %)
            </div>
            <div>
              <Text strong>Validation:</Text> {validationSet.length} examples (
              {validationSet.length > 0
                ? Math.round((validationSet.length / selectedData.length) * 100)
                : 0}
              %)
            </div>
            <div>
              <Text strong>Testing:</Text> {testingSet.length} examples (
              {testingSet.length > 0
                ? Math.round((testingSet.length / selectedData.length) * 100)
                : 0}
              %)
            </div>
          </div>

          <div
            style={{
              display: "flex",
              gap: "12px",
              justifyContent: "center",
              marginTop: "24px",
            }}
          >
            <Button
              type="primary"
              onClick={() => {
                const shuffled = [...selectedData].sort(
                  () => Math.random() - 0.5
                );
                const trainSize = Math.floor(shuffled.length * 0.7);
                const valSize = Math.floor(shuffled.length * 0.15);
                setTrainingSet(shuffled.slice(0, trainSize));
                setValidationSet(
                  shuffled.slice(trainSize, trainSize + valSize)
                );
                setTestingSet(shuffled.slice(trainSize + valSize));
              }}
            >
              Auto-Split (70/15/15)
            </Button>
            <Button
              onClick={() => {
                setTrainingSet([]);
                setValidationSet([]);
                setTestingSet([]);
              }}
            >
              Clear All
            </Button>
          </div>
        </Space>
      </Card>

      <Row gutter={[16, 16]}>
        <Col xs={24} md={8}>
          <Card
            title={<span style={{ color: "#0071e3" }}>Training Set</span>}
            size="small"
          >
            <Paragraph>AI will learn patterns from these examples</Paragraph>
            <Paragraph>
              <Text strong>{trainingSet.length} examples</Text>
            </Paragraph>
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card
            title={<span style={{ color: "#0071e3" }}>Validation Set</span>}
            size="small"
          >
            <Paragraph>Used during training to check progress</Paragraph>
            <Paragraph>
              <Text strong>{validationSet.length} examples</Text>
            </Paragraph>
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card
            title={<span style={{ color: "#0071e3" }}>Testing Set</span>}
            size="small"
          >
            <Paragraph>Final evaluation on completely new data</Paragraph>
            <Paragraph>
              <Text strong>{testingSet.length} examples</Text>
            </Paragraph>
          </Card>
        </Col>
      </Row>

      <Card
        style={{
          backgroundColor:
            trainingSet.length >= 6 &&
            validationSet.length >= 1 &&
            testingSet.length >= 2
              ? "#f6ffed"
              : "#fffbe6",
          borderColor:
            trainingSet.length >= 6 &&
            validationSet.length >= 1 &&
            testingSet.length >= 2
              ? "#52c41a"
              : "#faad14",
        }}
      >
        <Paragraph style={{ marginBottom: 0 }}>
          {trainingSet.length >= 6 &&
          validationSet.length >= 1 &&
          testingSet.length >= 2 ? (
            <Text>
              Great split! You have enough examples in each set for effective
              training. Proceed to the next step.
            </Text>
          ) : (
            <Text strong>
              ⚠️ Minimum recommended: 6 training, 2 validation, 2 testing
              examples. You have: {trainingSet.length} training,{" "}
              {validationSet.length} validation, {testingSet.length} testing.
            </Text>
          )}
        </Paragraph>
      </Card>
    </Space>
  );

  const renderSettings = () => (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Card
        title={
          <span style={{ color: "#0071e3" }}>
            Understanding Training Settings
          </span>
        }
      >
        <Paragraph style={{ marginBottom: "8px" }}>
          <Text>Think of learning to ride a bike:</Text>
        </Paragraph>
        <ul style={{ paddingLeft: "20px", marginBottom: 0 }}>
          <li>
            <Text strong>Epochs:</Text> How many practice sessions you do
          </li>
          <li>
            <Text strong>Batch Size:</Text> How many skills you try before
            checking progress
          </li>
          <li>
            <Text strong>Learning Rate:</Text> How big of adjustments you make
            each time
          </li>
        </ul>
      </Card>

      <Row gutter={[16, 16]}>
        <Col xs={24} md={8}>
          <Card
            title={<span style={{ color: "#0071e3" }}>Epochs: {epochs}</span>}
          >
            <Slider
              min={5}
              max={30}
              value={epochs}
              onChange={setEpochs}
              // marks={{
              //   5: "5 (Quick)",
              //   15: "15 (Balanced)",
              //   30: "30 (Thorough)",
              // }}
            />
            <Paragraph
              style={{ marginTop: "16px", marginBottom: 0, fontSize: "14px" }}
            >
              Quick → Balanced → Thorough
            </Paragraph>
            <Paragraph
              style={{ marginTop: "16px", marginBottom: 0, fontSize: "14px" }}
            >
              More epochs = more practice time, but too many can cause
              memorization instead of learning
            </Paragraph>
          </Card>
        </Col>

        <Col xs={24} md={8}>
          <Card
            title={
              <span style={{ color: "#0071e3" }}>Batch Size: {batchSize}</span>
            }
          >
            <Slider
              min={1}
              max={Math.max(1, Math.floor(trainingSet.length / 2))}
              value={batchSize}
              onChange={setBatchSize}
              // marks={{
              //   1: "1",
              //   [Math.floor(trainingSet.length / 4) || 1]:
              //     Math.floor(trainingSet.length / 4) || 1,
              //   [Math.floor(trainingSet.length / 2) || 1]:
              //     Math.floor(trainingSet.length / 2) || 1,
              // }}
            />
            <Paragraph
              style={{ marginTop: "16px", marginBottom: 0, fontSize: "14px" }}
            >
              Smaller → Larger
            </Paragraph>
            <Paragraph
              style={{ marginTop: "16px", marginBottom: 0, fontSize: "14px" }}
            >
              Smaller batches = more frequent updates (good for learning but
              noisier)
            </Paragraph>
          </Card>
        </Col>

        <Col xs={24} md={8}>
          <Card
            title={
              <span style={{ color: "#0071e3" }}>
                Learning Rate: {learningRate.toFixed(2)}
              </span>
            }
          >
            <Slider
              min={0.01}
              max={0.3}
              step={0.01}
              value={learningRate}
              onChange={setLearningRate}
              // marks={{
              //   0.01: "0.01 (Careful)",
              //   0.1: "0.1 (Moderate)",
              //   0.3: "0.3 (Aggressive)",
              // }}
            />
            <Paragraph
              style={{ marginTop: "16px", marginBottom: 0, fontSize: "14px" }}
            >
              Careful → Moderate → Aggressive
            </Paragraph>
            <Paragraph
              style={{ marginTop: "16px", marginBottom: 0, fontSize: "14px" }}
            >
              Controls how big each learning step is - like how much to correct
              after each mistake
            </Paragraph>
          </Card>
        </Col>
      </Row>

      <Card
        title={<span style={{ color: "#0071e3" }}>Training Plan Summary</span>}
      >
        <Space direction="vertical" size="small">
          <Text>Training on {trainingSet.length} examples</Text>
          <Text>Validating on {validationSet.length} examples</Text>
          <Text>Testing on {testingSet.length} examples</Text>
          <Text>Will train on dataset {epochs} times</Text>
          <Text>Processing {batchSize} examples at a time</Text>
          <Text>Learning rate: {learningRate}</Text>
        </Space>
      </Card>
    </Space>
  );

  const renderTraining = () => (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Card
        title={
          <span style={{ color: "#0071e3" }}>
            How AI Learns (Training Process)
          </span>
        }
      >
        <Paragraph style={{ marginBottom: "8px" }}>
          <Text>The Learning Loop - what happens in each epoch:</Text>
        </Paragraph>
        <ol style={{ paddingLeft: "20px", marginBottom: 0 }}>
          <li>
            <Text strong>Look at Examples:</Text> AI examines training recipes
          </li>
          <li>
            <Text strong>Make Predictions:</Text> Guesses what each recipe is
          </li>
          <li>
            <Text strong>Check Mistakes:</Text> Compares guesses to correct
            answers
          </li>
          <li>
            <Text strong>Adjust & Learn:</Text> Changes internal understanding
            to improve
          </li>
          <li>
            <Text strong>Repeat:</Text> Does this over and over until it gets
            good!
          </li>
        </ol>
      </Card>

      <Card>
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Title level={5}>Training Configuration</Title>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
              gap: "12px",
            }}
          >
            <div>{trainingSet.length} training examples</div>
            <div>{validationSet.length} validation examples</div>
            <div>{testingSet.length} testing examples</div>
            <div>{epochs} epochs</div>
            <div>Batch size: {batchSize}</div>
            <div>Learning rate: {learningRate}</div>
          </div>
          <div
            style={{
              display: "flex",
              gap: "12px",
              justifyContent: "center",
              marginTop: "24px",
            }}
          >
            <Button type="primary" onClick={trainModel} disabled={isTraining}>
              {isTraining
                ? "Training..."
                : modelTrained
                ? "Train Again"
                : "Start Training!"}
            </Button>
          </div>
        </Space>
      </Card>

      {(isTraining || modelTrained) && (
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <Card>
            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <Text>
                  Training Progress: Epoch {currentEpoch} of {epochs}
                </Text>
                <Text>{isTraining ? "🔄 Learning..." : "✅ Complete!"}</Text>
              </div>
              <Progress
                percent={Math.round((currentEpoch / epochs) * 100)}
                status={isTraining ? "active" : "success"}
              />
            </Space>
          </Card>

          <Row gutter={[16, 16]}>
            <Col xs={24} md={8}>
              <Card>
                <div style={{ textAlign: "center" }}>
                  <div
                    style={{
                      fontSize: "32px",
                      fontWeight: 700,
                      color: "#0071e3",
                    }}
                  >
                    {trainingAccuracy.length > 0
                      ? `${trainingAccuracy[
                          trainingAccuracy.length - 1
                        ].toFixed(1)}%`
                      : "0%"}
                  </div>
                  <div style={{ fontSize: "14px", marginTop: "8px" }}>
                    Training Accuracy
                  </div>
                </div>
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card>
                <div style={{ textAlign: "center" }}>
                  <div
                    style={{
                      fontSize: "32px",
                      fontWeight: 700,
                      color: "#0071e3",
                    }}
                  >
                    {validationAccuracy.length > 0
                      ? `${validationAccuracy[
                          validationAccuracy.length - 1
                        ].toFixed(1)}%`
                      : "0%"}
                  </div>
                  <div style={{ fontSize: "14px", marginTop: "8px" }}>
                    Validation Accuracy
                  </div>
                </div>
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card>
                <div style={{ textAlign: "center" }}>
                  <div
                    style={{
                      fontSize: "32px",
                      fontWeight: 700,
                      color: "#0071e3",
                    }}
                  >
                    {currentEpoch}
                  </div>
                  <div style={{ fontSize: "14px", marginTop: "8px" }}>
                    Current Epoch
                  </div>
                </div>
              </Card>
            </Col>
          </Row>

          {showOverfitting && (
            <Card
              style={{ backgroundColor: "#fff7e6", borderColor: "#faad14" }}
            >
              <Paragraph style={{ marginBottom: 0 }}>
                <Text strong>⚠️ Overfitting Detected!</Text> Training accuracy
                is high but validation accuracy is low. The AI is memorizing
                training examples instead of learning general patterns. Try
                reducing epochs or adding more data.
              </Paragraph>
            </Card>
          )}

          {modelTrained && (
            <Card
              style={{ backgroundColor: "#f6ffed", borderColor: "#52c41a" }}
            >
              <Title level={4}>Training Complete!</Title>
              <Paragraph style={{ marginBottom: 0 }}>
                {validationAccuracy[validationAccuracy.length - 1] >= 70
                  ? "Great! The model learned well and performs well on validation data. Proceed to the next step."
                  : "The model could use improvement. Consider adjusting settings or adding more data."}
              </Paragraph>
            </Card>
          )}
        </Space>
      )}
    </Space>
  );

  const renderTesting = () => (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Card
        title={
          <span style={{ color: "#0071e3" }}>Understanding Model Testing</span>
        }
      >
        <Paragraph style={{ marginBottom: "8px" }}>
          <Text>Three types of data in ML:</Text>
        </Paragraph>
        <ul style={{ paddingLeft: "20px", marginBottom: 0 }}>
          <li>
            <Text strong>Training Data:</Text> Used to teach the model (already
            used)
          </li>
          <li>
            <Text strong>Validation Data:</Text> Used during training to check
            progress (seen during training)
          </li>
          <li>
            <Text strong>Testing Data:</Text> Completely NEW data the model has
            never seen (what we test with now)
          </li>
        </ul>
      </Card>

      <Card
        title={
          <span style={{ color: "#0071e3" }}>Test Individual Example</span>
        }
      >
        <Paragraph>
          Select a specific recipe from your <Text strong>Testing Set</Text> (
          {testingSet.length} examples). These are completely new recipes the AI
          has never seen before!
        </Paragraph>

        <Select
          value={individualTestId}
          onChange={setIndividualTestId}
          style={{ width: "100%", marginBottom: "16px" }}
          placeholder="Select a test example"
          size="large"
        >
          {testingSet.map((id) => {
            const point = allDataPoints.find((d) => d.id === id);
            return (
              <Option key={id} value={id}>
                {point.emoji} Example #{id} - {point.label}
              </Option>
            );
          })}
        </Select>

        <div
          style={{
            display: "flex",
            gap: "12px",
            justifyContent: "center",
          }}
        >
          <Button
            type="primary"
            onClick={() => {
              if (individualTestId) {
                const point = allDataPoints.find(
                  (d) => d.id === individualTestId
                );
                const learnedExamples = trainingSet.map((id) => {
                  const p = allDataPoints.find((d) => d.id === id);
                  return { dataPoint: p };
                });
                const prediction = predict(
                  point,
                  weights,
                  bias,
                  learnedExamples
                );
                setIndividualTestResult({
                  dataPoint: point,
                  prediction: prediction.prediction,
                  confidence: prediction.confidence,
                  correct: prediction.prediction === point.label,
                });
              }
            }}
            disabled={!individualTestId}
          >
            Test This Example
          </Button>
        </div>

        {individualTestResult && (
          <Card
            style={{
              marginTop: "16px",
              borderColor: individualTestResult.correct ? "#52c41a" : "#ff4d4f",
              borderWidth: "2px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                marginBottom: "16px",
              }}
            >
              <span style={{ fontSize: "48px" }}>
                {individualTestResult.dataPoint.emoji}
              </span>
              <Title level={4} style={{ margin: 0 }}>
                {individualTestResult.correct
                  ? "✅ Correct Prediction"
                  : "❌ Wrong Prediction"}
              </Title>
            </div>
            <Space direction="vertical" size="small" style={{ width: "100%" }}>
              <div>
                <Text>Actual Recipe: </Text>
                <Text strong>{individualTestResult.dataPoint.label}</Text>
              </div>
              <div>
                <Text>Model Predicted: </Text>
                <Text
                  strong
                  style={{
                    color: individualTestResult.correct ? "#52c41a" : "#ff4d4f",
                  }}
                >
                  {individualTestResult.prediction}
                </Text>
              </div>
              <div>
                <Text>Confidence: </Text>
                <Text strong>{individualTestResult.confidence}%</Text>
              </div>
              <div style={{ marginTop: "12px" }}>
                <Text strong>Ingredients (Features):</Text>
                <div style={{ marginTop: "8px" }}>
                  <div>🍯 Sugar: {individualTestResult.dataPoint.sugar}/10</div>
                  <div>🌾 Flour: {individualTestResult.dataPoint.flour}/10</div>
                  <div>
                    🧈 Butter: {individualTestResult.dataPoint.butter}/10
                  </div>
                  <div>🥚 Eggs: {individualTestResult.dataPoint.eggs}</div>
                </div>
              </div>
            </Space>
          </Card>
        )}
      </Card>

      <Card title={<span style={{ color: "#0071e3" }}>Test All Examples</span>}>
        <Paragraph>
          Run the model on{" "}
          <Text strong>all {testingSet.length} testing examples</Text> to see
          overall performance on completely new data.
        </Paragraph>

        <Row gutter={[16, 16]} style={{ marginBottom: "16px" }}>
          <Col xs={8}>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: "24px", fontWeight: 700 }}>
                {testingSet.length}
              </div>
              <div style={{ fontSize: "12px" }}>Testing Examples</div>
            </div>
          </Col>
          <Col xs={8}>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: "24px", fontWeight: 700 }}>
                {trainingSet.length}
              </div>
              <div style={{ fontSize: "12px" }}>Trained On</div>
            </div>
          </Col>
          <Col xs={8}>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: "24px", fontWeight: 700 }}>
                {validationSet.length}
              </div>
              <div style={{ fontSize: "12px" }}>Validation Set</div>
            </div>
          </Col>
        </Row>

        <div
          style={{
            display: "flex",
            gap: "12px",
            justifyContent: "center",
            marginTop: "24px",
          }}
        >
          <Button type="primary" onClick={testModelOnSet}>
            Run Full Test on {testingSet.length} Unseen Examples
          </Button>
        </div>
      </Card>

      {testResults.length > 0 && (
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={8}>
              <Card>
                <div style={{ textAlign: "center" }}>
                  <div
                    style={{
                      fontSize: "28px",
                      fontWeight: 700,
                      color: "#0071e3",
                    }}
                  >
                    {trainingAccuracy[trainingAccuracy.length - 1]?.toFixed(1)}%
                  </div>
                  <div style={{ fontSize: "14px", marginTop: "8px" }}>
                    Training Accuracy
                  </div>
                </div>
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card>
                <div style={{ textAlign: "center" }}>
                  <div
                    style={{
                      fontSize: "28px",
                      fontWeight: 700,
                      color: "#0071e3",
                    }}
                  >
                    {validationAccuracy[validationAccuracy.length - 1]?.toFixed(
                      1
                    )}
                    %
                  </div>
                  <div style={{ fontSize: "14px", marginTop: "8px" }}>
                    Validation Accuracy
                  </div>
                </div>
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card style={{ borderColor: "#52c41a", borderWidth: "2px" }}>
                <div style={{ textAlign: "center" }}>
                  <div
                    style={{
                      fontSize: "28px",
                      fontWeight: 700,
                      color: "#52c41a",
                    }}
                  >
                    {finalTestAccuracy.toFixed(1)}%
                  </div>
                  <div style={{ fontSize: "14px", marginTop: "8px" }}>
                    Test Accuracy
                  </div>
                </div>
              </Card>
            </Col>
          </Row>

          <Card
            title={
              <span style={{ color: "#0071e3" }}>Individual Predictions</span>
            }
          >
            <Row gutter={[16, 16]}>
              {testResults.map((result, idx) => (
                <Col xs={12} md={8} key={idx}>
                  <Card
                    size="small"
                    style={{
                      borderColor: result.correct ? "#52c41a" : "#ff4d4f",
                      borderWidth: "2px",
                    }}
                  >
                    <div style={{ textAlign: "center" }}>
                      <div style={{ fontSize: "32px", marginBottom: "8px" }}>
                        {result.dataPoint.emoji} {result.correct ? "✅" : "❌"}
                      </div>
                      <div style={{ fontSize: "12px" }}>
                        <div>
                          Actual: <Text strong>{result.dataPoint.label}</Text>
                        </div>
                        <div>
                          Predicted: <Text strong>{result.prediction}</Text>
                        </div>
                        <div>
                          Confidence: <Text strong>{result.confidence}%</Text>
                        </div>
                      </div>
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>
          </Card>

          <Card
            title={
              <span style={{ color: "#0071e3" }}>Performance Analysis</span>
            }
            style={{
              borderColor: finalTestAccuracy >= 70 ? "#52c41a" : "#faad14",
            }}
          >
            <Paragraph>
              {finalTestAccuracy >= 70 ? (
                <Text>
                  🎉 Great Performance! <br/> Your model learned real
                  patterns and generalizes well to new data. The training,
                  validation, and testing accuracies are all good.
                </Text>
              ) : (
                <Text>
                  ⚠️ Needs Improvement. <br/> The model could perform better. Consider
                  adding more data, adjusting hyperparameters, or training for
                  more epochs.
                </Text>
              )}
            </Paragraph>
          </Card>
        </Space>
      )}
    </Space>
  );

  const tabItems = [
    {
      key: "dataset",
      label: "Build Dataset",
      children: renderDataset(),
    },
    {
      key: "split",
      label: "Split Data",
      children: renderSplitData(),
    },
    {
      key: "settings",
      label: "Training Settings",
      children: renderSettings(),
    },
    {
      key: "train",
      label: "Train Model",
      children: renderTraining(),
    },
    {
      key: "test",
      label: "Test & Evaluate",
      children: renderTesting(),
    },
  ];

  return (
    <div className="page-container">
      <header
        style={{ textAlign: "center", marginBottom: "32px" }}
        className="fade-in-down"
      >
        <Title level={1}>Model Training and Testing</Title>
        <Paragraph style={{ fontSize: "18px" }}>
          Follow these 5 steps to train an AI model that can recognize recipes
          from their ingredients.
        </Paragraph>
      </header>

      <Tabs defaultActiveKey="dataset" items={tabItems} size="large" />
    </div>
  );
};

export default ModelTraining;
